import { BulkBanResult, BulkBanSkipReason, BulkBanSummary } from 'types/interfaces/admin/bulkBan';

/**
 * Reason presets (Decision 5, Asana 1218655440141997). Frontend constants; the
 * backend only enforces length. Every preset is sent verbatim as the reason,
 * so each must stay at 10+ characters, which is banUser's minimum.
 */
export const BULK_BAN_REASON_PRESETS = ['SMS_PUMPING', 'DUPLICATE_ACCOUNT', 'ONBOARDING_ABUSE'] as const;
export const BULK_BAN_REASON_OTHER = 'OTHER';
export const BULK_BAN_DEFAULT_REASON = BULK_BAN_REASON_PRESETS[0];

/**
 * Shown when an apply call fails with no backend message (network drop, gateway
 * timeout, 5xx). It must not claim that nothing was banned: the backend bans
 * sequentially, one transaction per id, so the loop may still be running or be
 * partly done when the response is lost. Only a 400 guarantees no writes, and a
 * 400 carries its own accurate message.
 */
export const BULK_BAN_REQUEST_FAILED_MESSAGE =
  'Bulk ban request failed. Refresh the list to check which accounts were banned.';

export const BULK_BAN_REASON_MIN = 10;
export const BULK_BAN_REASON_MAX = 1000;

/** The reason string to send, or null while the current choice is not yet valid. */
export const resolveBulkBanReason = (choice: string, otherText: string): string | null => {
  if (choice !== BULK_BAN_REASON_OTHER) return choice;
  const trimmed = otherText.trim();
  if (trimmed.length < BULK_BAN_REASON_MIN || trimmed.length > BULK_BAN_REASON_MAX) return null;
  return trimmed;
};

// Labels for the flags in Decision 4. An unknown flag renders as its raw string.
const FLAG_LABELS: Record<string, string> = {
  points_gt_0: 'has points',
  email_verified: 'email verified',
  phone_verified: 'phone verified',
  has_trades: 'has trades',
  has_offers: 'has offers',
  late_login: 'later login',
};

export const bulkBanFlagLabel = (flag: string): string =>
  Object.prototype.hasOwnProperty.call(FLAG_LABELS, flag) ? FLAG_LABELS[flag] : flag;

const SKIP_REASON_LABELS: Record<BulkBanSkipReason, string> = {
  already_banned: 'already banned',
  deleted: 'deleted account',
  protected: 'protected admin account',
  self: 'your own account',
};

/** One short phrase saying why a row was not, or will not be, banned. */
export const bulkBanRowNote = (row: BulkBanResult): string => {
  if (row.outcome === 'missing') return 'no such user';
  if (row.outcome === 'skipped') return row.skipReason ? SKIP_REASON_LABELS[row.skipReason] ?? row.skipReason : 'skipped';
  if (row.outcome === 'failed') return row.message || 'failed';
  return '';
};

export const isFlaggedBannable = (row: BulkBanResult): boolean =>
  row.outcome === 'would_ban' && row.flags.length > 0;

/**
 * Preview order (Decision 8): flagged bannable rows first, then clean bannable
 * rows, then everything that will not be banned. Request order is kept inside
 * each group, so the sort is stable on purpose.
 */
export const sortBulkBanPreview = (results: readonly BulkBanResult[]): BulkBanResult[] => {
  const rank = (row: BulkBanResult): number => {
    if (isFlaggedBannable(row)) return 0;
    if (row.outcome === 'would_ban') return 1;
    return 2;
  };
  return results
    .map((row, index) => ({ row, index }))
    .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
    .map(({ row }) => row);
};

/**
 * Toast headline, final copy from Decision 9. X is banned, N is the unique ids
 * requested, Y folds missing ids into skipped (they are labeled missing in the
 * expanded list), Z is failed.
 */
export const bulkBanToastHeadline = (summary: BulkBanSummary): string =>
  `Banned ${summary.banned} of ${summary.total}. ${summary.skipped + summary.missing} skipped, ${summary.failed} failed.`;

/** Rows for the toast's expandable list: everything that was not banned. */
export const bulkBanToastDetails = (results: readonly BulkBanResult[]): Array<{ id: number; text: string }> =>
  results
    .filter((row) => row.outcome !== 'banned')
    .map((row) => {
      const who = row.username ? `${row.username} (#${row.id})` : `#${row.id}`;
      const kind = row.outcome === 'missing' ? 'Missing' : row.outcome === 'failed' ? 'Failed' : 'Skipped';
      return { id: row.id, text: `${kind}: ${who}, ${bulkBanRowNote(row)}` };
    });
